import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "./api/client";
import {
  fetchClassSchedule,
  fetchTenant,
  listClassSessions,
  listCourts,
  listSports,
} from "./api/endpoints";
import {
  addDays,
  buildScheduleEntries,
  formatDayNumber,
  formatWeekdayShort,
  mondayOf,
  toDateOnly,
  weekDates,
  type ScheduleEntry,
} from "./schedule";
import type { CourtDto, SportDto } from "../types";

export type ScheduleStatus = "loading" | "ready" | "error" | "denied";

export interface ScheduleDay {
  date: string;
  weekday: string;
  dayNumber: string;
  isToday: boolean;
}

export interface UseClassScheduleResult {
  status: ScheduleStatus;
  error: string | null;
  weekLoading: boolean;
  weekStart: string | null;
  days: ScheduleDay[];
  today: string | null;
  isCurrentWeek: boolean;
  courts: CourtDto[];
  court: CourtDto | null;
  selectedCourtId: string | null;
  selectCourt: (courtId: string) => void;
  sports: SportDto[];
  entries: ScheduleEntry[];
  goToPreviousWeek: () => void;
  goToNextWeek: () => void;
  goToCurrentWeek: () => void;
  refresh: () => void;
  retry: () => void;
}

interface UseClassScheduleOptions {
  token: string;
  tenantId: string;
}

const DENIED_MESSAGE = "As aulas deste CT ainda não estão liberadas para o seu perfil.";
const GENERIC_ERROR = "Não foi possível carregar a agenda do CT.";
const SESSION_ERROR = "Sua sessão expirou. Entre novamente para ver a agenda.";

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

function classifyError(error: unknown): { status: ScheduleStatus; message: string } {
  if (error instanceof ApiError) {
    const code = typeof error.extensions?.code === "string" ? error.extensions.code : "";
    if (error.status === 403 || code === "CLASS_READ_DENIED" || code === "CLASS_SESSION_READ_DENIED") {
      return { status: "denied", message: DENIED_MESSAGE };
    }
    if (error.status === 401) return { status: "error", message: SESSION_ERROR };
  }
  return { status: "error", message: GENERIC_ERROR };
}

/**
 * Carrega a agenda semanal do CT ativo.
 * O fuso vem de `GET /api/tenants/{id}`; o cache curto é indexado por
 * `tenant + semana + quadra` e é descartado na troca de CT e no refresh.
 */
export function useClassSchedule({ token, tenantId }: UseClassScheduleOptions): UseClassScheduleResult {
  const [status, setStatus] = useState<ScheduleStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [weekLoading, setWeekLoading] = useState(false);
  const [tenantZone, setTenantZone] = useState<string | null>(null);
  const [today, setToday] = useState<string | null>(null);
  const [weekStart, setWeekStart] = useState<string | null>(null);
  const [courts, setCourts] = useState<CourtDto[]>([]);
  const [sports, setSports] = useState<SportDto[]>([]);
  const [selectedCourtId, setSelectedCourtId] = useState<string | null>(null);
  const [entries, setEntries] = useState<ScheduleEntry[]>([]);
  const [catalogReloadKey, setCatalogReloadKey] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const cache = useRef(new Map<string, ScheduleEntry[]>());

  useEffect(() => {
    cache.current.clear();
  }, [tenantId]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setStatus("loading");
    setError(null);
    setWeekLoading(false);
    setTenantZone(null);
    setToday(null);
    setWeekStart(null);
    setCourts([]);
    setSports([]);
    setSelectedCourtId(null);
    setEntries([]);

    Promise.all([
      fetchTenant(token, tenantId, controller.signal),
      listCourts(token, controller.signal),
      listSports(token, controller.signal),
    ])
      .then(([tenant, courtPage, sportPage]) => {
        if (!active) return;
        const zone = tenant.timeZoneId;
        const localToday = toDateOnly(new Date(), zone);
        setTenantZone(zone);
        setToday(localToday);
        setWeekStart(mondayOf(localToday));
        setCourts(courtPage.items);
        setSports(sportPage.items);
        setSelectedCourtId(courtPage.items[0]?.id ?? null);
      })
      .catch(caught => {
        if (!active || isAbortError(caught)) return;
        const classified = classifyError(caught);
        setStatus(classified.status);
        setError(classified.message);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [token, tenantId, catalogReloadKey]);

  useEffect(() => {
    if (!weekStart || !tenantZone) return;
    if (!selectedCourtId) {
      setEntries([]);
      setStatus("ready");
      setWeekLoading(false);
      return;
    }

    const key = `${tenantId}|${weekStart}|${selectedCourtId}`;
    const cached = cache.current.get(key);
    if (cached) {
      setEntries(cached);
      setError(null);
      setStatus("ready");
      setWeekLoading(false);
      return;
    }

    const controller = new AbortController();
    let active = true;
    const court = courts.find(item => item.id === selectedCourtId);
    setEntries([]);
    setWeekLoading(true);
    setError(null);

    Promise.all([
      fetchClassSchedule(token, {
        weekStart,
        courtId: selectedCourtId,
        signal: controller.signal,
      }),
      listClassSessions(token, {
        startDate: weekStart,
        endDate: addDays(weekStart, 6),
        courtId: selectedCourtId,
        signal: controller.signal,
      }),
    ])
      .then(([schedule, sessions]) => {
        if (!active) return;
        const built = buildScheduleEntries(schedule, sessions, {
          courtName: court?.name ?? "",
          timeZoneId: schedule.timeZoneId,
          sports,
        });
        cache.current.set(key, built);
        setEntries(built);
        setStatus("ready");
      })
      .catch(caught => {
        if (!active || isAbortError(caught)) return;
        const classified = classifyError(caught);
        setStatus(classified.status);
        setError(classified.message);
      })
      .finally(() => {
        if (active) setWeekLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [token, tenantId, tenantZone, weekStart, selectedCourtId, courts, sports, reloadKey]);

  const selectCourt = useCallback((courtId: string) => {
    setSelectedCourtId(current => (current === courtId ? current : courtId));
  }, []);

  const goToPreviousWeek = useCallback(() => {
    setWeekStart(current => (current ? addDays(current, -7) : current));
  }, []);

  const goToNextWeek = useCallback(() => {
    setWeekStart(current => (current ? addDays(current, 7) : current));
  }, []);

  const goToCurrentWeek = useCallback(() => {
    if (!tenantZone) return;
    setWeekStart(mondayOf(toDateOnly(new Date(), tenantZone)));
  }, [tenantZone]);

  const refresh = useCallback(() => {
    cache.current.clear();
    setReloadKey(current => current + 1);
  }, []);

  const retry = useCallback(() => {
    cache.current.clear();
    setCatalogReloadKey(current => current + 1);
    setReloadKey(current => current + 1);
  }, []);

  const days = useMemo<ScheduleDay[]>(() => {
    if (!weekStart) return [];
    return weekDates(weekStart).map(date => ({
      date,
      weekday: formatWeekdayShort(date),
      dayNumber: formatDayNumber(date),
      isToday: date === today,
    }));
  }, [weekStart, today]);

  const currentWeekStart = tenantZone && today ? mondayOf(today) : null;
  const court = courts.find(item => item.id === selectedCourtId) ?? null;

  return {
    status,
    error,
    weekLoading,
    weekStart,
    days,
    today,
    isCurrentWeek: Boolean(weekStart && currentWeekStart && weekStart === currentWeekStart),
    courts,
    court,
    selectedCourtId,
    selectCourt,
    sports,
    entries,
    goToPreviousWeek,
    goToNextWeek,
    goToCurrentWeek,
    refresh,
    retry,
  };
}
